// Xem ngày tốt theo VIỆC (cưới hỏi, mua xe, khai trương…) — công cụ miễn phí, tất
// định, đứng riêng ngoài Vận khí. Mọi phép tính ở BE (`/v1/xem-ngay`); trang này chỉ
// chọn việc, người, hướng / màu, khoảng ngày rồi hiển thị.
//
// Hai màn: `?viec=` rỗng → lưới chọn việc; có `viec` → trang riêng của việc đó.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowRight,
  Car,
  Check,
  Clock,
  Compass,
  FileSignature,
  Gem,
  HeartHandshake,
  Home,
  KeyRound,
  Loader2,
  Palette,
  Pencil,
  Shovel,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Star,
  Store,
  User,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

import { AlmanacHeader, AlmanacLink } from "@/components/van-khi/almanac-layout";
import { IconVanKhi, IconXemNgay } from "@/components/nav-icons";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCompassHeading } from "@/hooks/use-compass-heading";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  listXemNgayPurposes,
  timXemNgay,
  type XemNgayBoTri,
  type XemNgayDay,
  type XemNgayHopTuoi,
  type XemNgayOptions,
  type XemNgayPurpose,
  type XemNgayResult,
  type XemNgayVerdict,
} from "@/lib/boide-api";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/xem-ngay")({
  validateSearch: (search: Record<string, unknown>): { viec?: string } => ({
    viec: typeof search.viec === "string" ? search.viec : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Xem ngày theo việc: cưới hỏi, nhà cửa, khai trương, xuất hành | Bói Đê" },
      {
        name: "description",
        content:
          "Lọc ngày theo việc bạn định làm và tuổi của người đứng tên: trực, sao hoàng đạo, ngày kỵ, xung hợp tuổi, hợp tuổi vợ chồng, hướng nhà Bát Trạch, Kim Lâu, Hoang Ốc, Tam Tai, hướng xuất hành.",
      },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/xem-ngay" }],
  }),
  component: XemNgayPage,
});

const PURPOSE_ICONS: Record<string, LucideIcon> = {
  "cuoi-hoi": HeartHandshake,
  "mua-xe": Car,
  "mua-nha": Home,
  "khai-truong": Store,
  "khoi-cong": Shovel,
  "nhap-trach": KeyRound,
  "ky-hop-dong": FileSignature,
  "mua-tai-san": Gem,
  "xuat-hanh": Compass,
};

const VERDICT: Record<XemNgayVerdict, { label: string; chip: string; cell: string }> = {
  tot: {
    label: "Nên chọn",
    chip: "border-element-wood/40 text-element-wood",
    cell: "bg-element-wood/80 text-white",
  },
  duoc: {
    label: "Tạm ổn",
    chip: "border-primary/40 text-primary",
    cell: "bg-primary/15 text-foreground",
  },
  tranh: {
    label: "Không nên",
    chip: "border-destructive/40 text-destructive",
    cell: "bg-destructive/10 text-muted-foreground",
  },
};

const COLOR_HEX: Record<string, string> = {
  Trắng: "#f5f5f4",
  Bạc: "#c0c4c8",
  Xám: "#8b8f94",
  Ghi: "#a3a3a3",
  Đen: "#1c1917",
  "Xanh dương": "#2563eb",
  "Xanh lá": "#2e9e4f",
  Đỏ: "#dc2626",
  Hồng: "#ec4899",
  Cam: "#f97316",
  Tím: "#7c3aed",
  Vàng: "#eab308",
  Nâu: "#8b5a2b",
  Be: "#e7d8b8",
};

type Person = { birth: string; gender: "nam" | "nu" | "" };

// Chủ đề Bói Đê để nhờ thầy luận. ⚠️ Chủ đề gộp `xem-ngay` đã TẮT từ v72 (tách
// thành 5 thẻ ngày) — trỏ vào nó là trang "Chủ đề không tồn tại hoặc đã tắt".
const THAY_TOPIC: Record<string, string> = {
  "cuoi-hoi": "ngay-cuoi-hoi",
  "khai-truong": "ngay-khai-truong",
  "ky-hop-dong": "ngay-khai-truong",
  "mua-nha": "ngay-dong-tho",
  "khoi-cong": "ngay-dong-tho",
  "nhap-trach": "ngay-dong-tho",
  "mua-xe": "ngay-mua-xe",
  "mua-tai-san": "ngay-mua-xe",
  "xuat-hanh": "ngay-xuat-hanh",
};

function XemNgayPage() {
  const { viec } = Route.useSearch();
  const navigate = Route.useNavigate();
  const listQuery = useQuery({
    queryKey: ["xem-ngay-purposes"],
    queryFn: listXemNgayPurposes,
    staleTime: 60 * 60 * 1000,
  });
  const purposes = listQuery.data?.purposes ?? [];
  const purpose = purposes.find((p) => p.key === viec) ?? null;

  return (
    <main className="almanac-page min-h-0 flex-1 overflow-y-auto">
      <div className="almanac-container">
        {purpose && listQuery.data ? (
          <PurposePage
            key={purpose.key}
            purpose={purpose}
            options={listQuery.data.options}
            profile={listQuery.data.profile ?? null}
            onBack={() => navigate({ search: {}, replace: true })}
          />
        ) : (
          <>
            <AlmanacHeader
              title="Xem ngày theo việc"
              description="Chọn việc quan trọng, tìm ngày phù hợp với bạn."
              icon={<IconXemNgay className="size-8" />}
            />
            <section aria-labelledby="purpose-picker-title" className="purpose-picker">
              <h2 id="purpose-picker-title" className="almanac-section-title">
                Bạn đang dự định điều gì?
              </h2>
              {listQuery.isLoading ? (
                <div className="purpose-grid" aria-busy="true" aria-label="Đang tải danh sách việc">
                  {Array.from({ length: 9 }, (_, i) => (
                    <Skeleton key={i} className="h-28 rounded-2xl" />
                  ))}
                </div>
              ) : listQuery.isError || !purposes.length ? (
                <div className="almanac-panel p-5" role="status">
                  <p className="text-sm text-muted-foreground">Chưa tải được danh sách việc.</p>
                  <Button
                    variant="outline"
                    onClick={() => void listQuery.refetch()}
                    className="mt-3"
                  >
                    Thử lại
                  </Button>
                </div>
              ) : (
                <div className="purpose-grid">
                  {purposes.map((p) => {
                    const Icon = PURPOSE_ICONS[p.key] ?? Sparkles;
                    return (
                      <Link
                        key={p.key}
                        to="/xem-ngay"
                        search={{ viec: p.key }}
                        className="purpose-tile"
                      >
                        <span className="purpose-tile-icon" aria-hidden="true">
                          <Icon className="size-6" strokeWidth={1.65} />
                        </span>
                        <span>{p.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
              <p className="purpose-note">
                <ShieldCheck className="size-4 shrink-0" aria-hidden="true" />
                Lọc theo lịch và tuổi của bạn
              </p>
            </section>
            <AlmanacLink
              to="/van-khi"
              title="Vận khí hôm nay"
              description="Xem điểm vận khí, giờ tốt và hướng xuất hành."
              icon={<IconVanKhi className="size-6" />}
            />
          </>
        )}
        <div className="mt-8">
          <SiteFooter />
        </div>
      </div>
    </main>
  );
}

function isoToday(): string {
  // Giờ VN: khách ở múi giờ khác vẫn thấy đúng "hôm nay" như BE.
  return new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
}

function PurposePage({
  purpose,
  options,
  profile,
  onBack,
}: {
  purpose: XemNgayPurpose;
  options?: XemNgayOptions;
  profile: { ten: string | null; birth_date: string; gioi_tinh: "nam" | "nu" | null } | null;
  onBack: () => void;
}) {
  const Icon = PURPOSE_ICONS[purpose.key] ?? Sparkles;
  const today = isoToday();

  // Hồ sơ tự điền vào vai đầu; với cưới hỏi thì điền đúng vai theo giới tính hồ sơ.
  const [people, setPeople] = useState<Person[]>(() =>
    purpose.people.map((r, i) => {
      const fits =
        profile &&
        (r.gioi_tinh ? r.gioi_tinh === profile.gioi_tinh : i === 0 && purpose.people.length === 1);
      return fits && profile
        ? { birth: profile.birth_date, gender: profile.gioi_tinh ?? "" }
        : { birth: "", gender: r.gioi_tinh ?? "" };
    }),
  );
  const [editing, setEditing] = useState<number | null>(null);
  const [sub, setSub] = useState(purpose.subs[0]?.key ?? "");
  const [range, setRange] = useState("6m");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [huong, setHuong] = useState<string>("");
  const [mau, setMau] = useState("");
  const [huongDi, setHuongDi] = useState("");
  const [picked, setPicked] = useState<string | null>(null);

  const search = useMutation({ mutationFn: timXemNgay });
  const run = () => {
    const births = people.map((p) => p.birth.trim());
    if (!births[0]) {
      toast.error(`Hãy nhập ${purpose.people[0]?.label.toLowerCase() ?? "năm sinh"} để xét tuổi`);
      setEditing(0);
      return;
    }
    search.mutate(
      {
        viec: purpose.key,
        khoang: range === "custom" ? undefined : range,
        tu: range === "custom" ? from : undefined,
        den: range === "custom" ? to : undefined,
        namSinh: births.filter(Boolean),
        gioiTinh: people.filter((p) => p.birth.trim()).map((p) => p.gender || null),
        viecCon: sub || undefined,
        huong: huong === "" ? null : Number(huong),
        mauXe: mau || null,
        huongDi: huongDi || null,
        dungHoSo: false,
      },
      {
        onError: (e) => toast.error(e instanceof Error ? e.message : "Chưa tìm được ngày"),
        onSuccess: (r) => setPicked(r.best[0]?.date ?? null),
      },
    );
  };

  return (
    <div>
      <button type="button" onClick={onBack} className="almanac-back">
        <ArrowLeft className="size-4" aria-hidden="true" />
        Chọn việc khác
      </button>
      <AlmanacHeader
        title={purpose.slogan}
        description={purpose.sub_slogan}
        icon={<Icon className="size-8" strokeWidth={1.65} />}
      />

      <section className="almanac-panel mt-6 flex flex-col gap-5 p-5 sm:p-6">
        <div className="divide-y divide-border/50">
          {purpose.people.map((role, i) => (
            <PersonRow
              key={role.role}
              label={role.label}
              optional={i > 0}
              fixedGender={role.gioi_tinh}
              person={people[i]}
              editing={editing === i}
              onEdit={() => setEditing(editing === i ? null : i)}
              onChange={(p) => setPeople((prev) => prev.map((x, j) => (j === i ? p : x)))}
            />
          ))}
        </div>

        {purpose.subs.length ? (
          <Field label="Cụ thể là">
            <select value={sub} onChange={(e) => setSub(e.target.value)} className={SELECT}>
              {purpose.subs.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        {purpose.inputs.includes("mau_xe") && options ? (
          <Field label="Xe màu gì?">
            <div className="flex flex-wrap gap-1.5">
              {options.mau_xe.map((c) => (
                <button
                  key={c.ten}
                  type="button"
                  onClick={() => setMau(mau === c.ten ? "" : c.ten)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition",
                    mau === c.ten ? "border-primary bg-primary/10 font-semibold" : "border-border",
                  )}
                >
                  <span
                    className="size-3.5 rounded-full border border-black/10"
                    style={{ backgroundColor: COLOR_HEX[c.ten] }}
                  />
                  {c.ten}
                </button>
              ))}
            </div>
          </Field>
        ) : null}

        {purpose.inputs.includes("huong_di") && options?.huong_di ? (
          <Field
            label="Bạn đi về phía nào?"
            hint="Chọn hướng nơi đến so với nhà bạn. Ngày có thần tốt ở đúng hướng ấy sẽ được đưa lên trước."
          >
            <div className="grid grid-cols-4 gap-1.5">
              {options.huong_di.map((h) => (
                <button
                  key={h.huong}
                  type="button"
                  onClick={() => setHuongDi(huongDi === h.huong ? "" : h.huong)}
                  className={cn(
                    "rounded-xl border px-1 py-2 text-[12px] transition",
                    huongDi === h.huong
                      ? "border-primary bg-primary/10 font-semibold text-primary"
                      : "border-border text-foreground/80",
                  )}
                >
                  {h.huong}
                </button>
              ))}
            </div>
          </Field>
        ) : null}

        {purpose.inputs.includes("huong") && options ? (
          <Field
            label={
              purpose.key === "khai-truong"
                ? "Cửa hàng quay về hướng nào?"
                : purpose.key === "nhap-trach"
                  ? "Cửa chính nhà / căn hộ quay về hướng nào?"
                  : "Nhà quay về hướng nào?"
            }
            hint="Bấm la bàn để đo ngay bằng điện thoại. Chưa đo được cũng không sao, bạn vẫn nhận được các hướng hợp cung phi."
          >
            <div className="flex gap-2">
              <select value={huong} onChange={(e) => setHuong(e.target.value)} className={SELECT}>
                <option value="">Chưa đo hướng</option>
                {options.huong.map((h) => (
                  <option key={h.son} value={h.goc}>
                    {h.range} ({h.huong} · sơn {h.son})
                  </option>
                ))}
              </select>
              <CompassPicker sons={options.huong} onPick={(goc) => setHuong(String(goc))} />
            </div>
          </Field>
        ) : null}

        <Field label="Tìm trong khoảng">
          <select value={range} onChange={(e) => setRange(e.target.value)} className={SELECT}>
            {(options?.khoang ?? []).map((k) => (
              <option key={k.key} value={k.key}>
                {k.label}
              </option>
            ))}
            <option value="custom">Tự chọn</option>
          </select>
          {range === "custom" ? (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          ) : null}
        </Field>

        <Button className="h-12 w-full text-base" onClick={run} disabled={search.isPending}>
          {search.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
          Lọc ngày cho tôi
        </Button>
      </section>

      {!search.data ? (
        <section className="mt-5">
          <h2 className="font-display text-lg font-bold text-foreground">Bạn sẽ nhận được</h2>
          <ul className="mt-2 space-y-1.5">
            {purpose.ket_qua.map((k) => (
              <li key={k} className="flex gap-2 text-sm text-foreground/85">
                <Check className="mt-0.5 size-4 shrink-0 text-element-wood" /> {k}
              </li>
            ))}
          </ul>
        </section>
      ) : (
        <Results result={search.data} picked={picked} onPick={setPicked} />
      )}
    </div>
  );
}

const SELECT =
  "h-11 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40";

/** Sơn (15°) gần nhất với một góc — ô chọn hướng dùng đúng tâm sơn làm giá trị. */
function nearestSon(deg: number, sons: XemNgayOptions["huong"]) {
  const step = Math.round((((deg % 360) + 360) % 360) / 15) % 24;
  return sons.find((s) => s.goc === step * 15) ?? sons[0];
}

/**
 * Đo hướng ngay tại trang: bật cảm biến, đứng đúng tư thế, bấm "Lấy hướng này" là
 * điền vào ô — không phải sang trang La bàn rồi quay lại. Máy không có cảm biến
 * (máy tính, vài dòng Android) thì gõ số độ đọc từ la bàn khác.
 */
function CompassPicker({
  sons,
  onPick,
}: {
  sons: XemNgayOptions["huong"];
  onPick: (goc: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState("");
  const { heading, trangThai, batLaBan, tatLaBan } = useCompassHeading();
  const live = heading === null ? null : Math.round(heading);
  const liveSon = live === null ? null : nearestSon(live, sons);
  const manualDeg = manual.trim() === "" ? null : Number(manual);
  const manualOk =
    manualDeg !== null && Number.isFinite(manualDeg) && manualDeg >= 0 && manualDeg < 360;
  const close = (next: boolean) => {
    setOpen(next);
    if (!next) tatLaBan();
  };
  const pick = (deg: number) => {
    onPick(nearestSon(deg, sons).goc);
    close(false);
  };
  const loi: Record<string, string> = {
    "tu-choi":
      "Bạn chưa cho phép đọc cảm biến hướng. Cấp quyền trong cài đặt trình duyệt rồi thử lại.",
    "khong-ho-tro": "Thiết bị này không có cảm biến hướng.",
    "khong-an-toan": "Trình duyệt chỉ cho đọc cảm biến trên trang https.",
    "khong-tin-hieu": "Không nhận được tín hiệu hướng từ máy.",
  };
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Đo hướng bằng la bàn"
        className="grid size-11 shrink-0 place-items-center rounded-xl border border-primary/40 bg-primary/5 text-primary"
      >
        <Compass className="size-5" />
      </button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Đo hướng cửa</DialogTitle>
            <DialogDescription>
              Đứng ngay cửa chính, lưng quay vào nhà, cầm điện thoại nằm ngang trước ngực, đầu máy
              chĩa thẳng ra ngoài. Tránh đứng sát cửa sắt, xe máy hay tủ lạnh.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center gap-3 py-2">
            <div className="relative grid size-40 place-items-center rounded-full border-2 border-gold-soft/60 bg-card">
              {["B", "Đ", "N", "T"].map((t, i) => (
                <span
                  key={t}
                  className="absolute text-[11px] font-bold text-muted-foreground"
                  style={{
                    transform: `rotate(${i * 90 - (live ?? 0)}deg) translateY(-62px) rotate(${-(i * 90 - (live ?? 0))}deg)`,
                  }}
                >
                  {t}
                </span>
              ))}
              <span
                className="absolute top-1.5 h-5 w-1 rounded-full bg-primary"
                aria-hidden="true"
              />
              <div className="text-center">
                <p className="font-display text-3xl font-bold text-foreground">
                  {live === null ? "—" : `${live}°`}
                </p>
                {liveSon ? (
                  <p className="text-[12px] text-muted-foreground">
                    {liveSon.huong} · sơn {liveSon.son}
                  </p>
                ) : null}
              </div>
            </div>

            {trangThai === "dang-chay" && live !== null ? (
              <Button className="w-full" onClick={() => pick(live)}>
                Lấy hướng này ({live}°)
              </Button>
            ) : trangThai === "dang-cho" ? (
              <p className="text-[12px] text-muted-foreground">Đang đọc cảm biến…</p>
            ) : (
              <Button className="w-full" variant="secondary" onClick={() => void batLaBan()}>
                Bật la bàn của máy
              </Button>
            )}
            {loi[trangThai] ? (
              <p className="text-center text-[12px] leading-5 text-amber-800 dark:text-amber-300">
                {loi[trangThai]}
              </p>
            ) : null}
          </div>

          <div className="border-t border-border/60 pt-3">
            <p className="text-[12px] text-muted-foreground">
              Đã có số đo từ la bàn khác? Gõ số độ (0–359):
            </p>
            <div className="mt-1.5 flex gap-2">
              <Input
                inputMode="numeric"
                value={manual}
                onChange={(e) => setManual(e.target.value.replace(/[^0-9.]/g, ""))}
                placeholder="Ví dụ 135"
              />
              <Button
                variant="outline"
                disabled={!manualOk}
                onClick={() => manualDeg !== null && pick(manualDeg)}
              >
                Dùng
              </Button>
            </div>
            {manualOk && manualDeg !== null ? (
              <p className="mt-1 text-[11px] text-muted-foreground">
                → {nearestSon(manualDeg, sons).huong} · sơn {nearestSon(manualDeg, sons).son}
              </p>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold text-foreground">{label}</p>
      {children}
      {hint ? <p className="mt-1.5 text-[11px] leading-4 text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function PersonRow({
  label,
  optional,
  fixedGender,
  person,
  editing,
  onEdit,
  onChange,
}: {
  label: string;
  optional: boolean;
  fixedGender: "nam" | "nu" | null;
  person: Person;
  editing: boolean;
  onEdit: () => void;
  onChange: (p: Person) => void;
}) {
  const hasBirth = Boolean(person.birth.trim());
  const shown = hasBirth
    ? person.birth.includes("-")
      ? person.birth.split("-").reverse().join("/")
      : `Năm ${person.birth}`
    : optional
      ? `Bổ sung ngày sinh ${label.toLowerCase()}`
      : `Nhập ngày sinh ${label.toLowerCase()}`;
  return (
    <div className="py-3 first:pt-0">
      <button type="button" onClick={onEdit} className="flex w-full items-center gap-3 text-left">
        <span className="grid size-12 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
          <User className="size-6" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2 text-xs font-semibold text-primary">
            {label}
            {optional ? (
              <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px]">
                Không bắt buộc
              </span>
            ) : null}
          </span>
          <span
            className={cn(
              "block text-sm",
              hasBirth ? "font-semibold text-foreground" : "text-muted-foreground",
            )}
          >
            {shown}
            {hasBirth && person.gender ? ` · ${person.gender === "nam" ? "Nam" : "Nữ"}` : ""}
          </span>
        </span>
        <Pencil className="size-4 shrink-0 text-primary" />
      </button>
      {editing ? (
        <div className="mt-3 grid grid-cols-[1fr_auto] gap-2 pl-15">
          <Input
            type="date"
            value={person.birth.includes("-") ? person.birth : ""}
            onChange={(e) => onChange({ ...person, birth: e.target.value })}
            aria-label={`Ngày sinh ${label}`}
          />
          {fixedGender ? null : (
            <select
              value={person.gender}
              onChange={(e) => onChange({ ...person, gender: e.target.value as Person["gender"] })}
              className={cn(SELECT, "w-24")}
              aria-label="Giới tính"
            >
              <option value="">Giới tính</option>
              <option value="nam">Nam</option>
              <option value="nu">Nữ</option>
            </select>
          )}
          <p className="col-span-2 text-[11px] text-muted-foreground">
            Nhập theo dương lịch; sinh trước Tết thì được tính vào năm âm trước đó
            {fixedGender ? "" : ", giới tính để tính cung phi"}.
          </p>
        </div>
      ) : null}
    </div>
  );
}

function Card({
  title,
  icon,
  hint,
  children,
}: {
  title: string;
  icon: ReactNode;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="almanac-panel p-4 sm:p-5">
      <h3 className="flex items-center gap-2 font-display text-base font-bold text-foreground">
        {icon}
        {title}
      </h3>
      {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Results({
  result,
  picked,
  onPick,
}: {
  result: XemNgayResult;
  picked: string | null;
  onPick: (d: string) => void;
}) {
  const pickedDay = result.days.find((d) => d.date === picked) ?? null;
  // Sang Bói Đê mang theo đủ thứ đã nhập ở đây: ngày đang chọn, ngày sinh + giới tính
  // từng người, và một câu hỏi soạn sẵn — người dùng chỉ còn thêm họ tên, giờ sinh.
  const askDay = pickedDay ?? result.best[0] ?? null;
  const askPeople = result.people
    .filter((p) => typeof p.nam_sinh === "string")
    .map((p) => `${p.nam_sinh}:${p.gioi_tinh === "nu" ? "nu" : "nam"}`)
    .join(",");
  const viec =
    result.purpose.label.toLowerCase() + (result.sub ? ` (${result.sub.label.toLowerCase()})` : "");
  const askQuestion = askDay
    ? `Tôi định ${viec} vào ${askDay.display} (${askDay.lunar_display.replace(" ÂL", " âm lịch")}). ` +
      `Ngày này có hợp với tuổi ${result.people.map((p) => p.can_chi).join(" và ") || "của tôi"} không, ` +
      `nên bắt đầu vào giờ nào và cần lưu ý gì?`
    : `Tôi định ${viec}, nhờ thầy chọn giúp ngày hợp tuổi.`;
  return (
    <div className="mt-5 space-y-5">
      {result.people.length ? (
        <Card title="Tuổi và mệnh" icon={<Users className="size-4 text-primary" />}>
          <ul className="space-y-2">
            {result.people.map((p) => (
              <li key={p.role} className="rounded-xl border border-border/60 p-3 text-[13px]">
                <p className="font-semibold text-foreground">
                  {p.label}: {p.can_chi} ({p.nam_sinh_am})
                </p>
                <p className="text-muted-foreground">
                  Mệnh {p.menh}
                  {p.cung_phi ? ` · cung phi ${p.cung_phi} (${p.nhom_menh})` : ""}
                </p>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {result.han_nam.length ? (
        <Card
          title="Hạn của năm"
          icon={<ShieldAlert className="size-4 text-primary" />}
          hint="Ba hạn này đi theo tuổi mụ cả năm, chọn ngày khác cũng không né được, nên biết trước để liệu."
        >
          <ul className="space-y-3">
            {result.han_nam.map((h) => (
              <li key={`${h.birth}-${h.nam}`} className="rounded-xl border border-border/60 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">
                  Năm {h.can_chi_nam} ({h.nam})
                </p>
                <p className="text-sm font-semibold text-foreground">
                  {h.label ? `${h.label} · ` : ""}tuổi {h.chi_tuoi} ({h.nam_sinh_am}) · tuổi mụ{" "}
                  {h.tuoi_mu}
                </p>
                {h.canh_bao.map((c) => (
                  <p
                    key={c}
                    className="mt-1 flex gap-1.5 text-[12px] leading-5 text-amber-800 dark:text-amber-300"
                  >
                    <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {c}
                  </p>
                ))}
                {h.on.map((c) => (
                  <p key={c} className="mt-1 flex gap-1.5 text-[12px] leading-5 text-element-wood">
                    <ShieldCheck className="mt-0.5 size-3.5 shrink-0" /> {c}
                  </p>
                ))}
              </li>
            ))}
          </ul>
          {result.nam_cuoi_dep?.length ? (
            <div className="mt-4">
              <p className="text-xs font-bold text-foreground">Sáu năm tới của cô dâu</p>
              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-6">
                {result.nam_cuoi_dep.map((y) => (
                  <div
                    key={y.nam}
                    className={cn(
                      "rounded-xl border p-2 text-center",
                      y.dep ? "border-element-wood/40 bg-element-wood/10" : "border-border/60",
                    )}
                  >
                    <p className="text-sm font-bold text-foreground">{y.nam}</p>
                    <p className="text-[10px] text-muted-foreground">{y.can_chi}</p>
                    <p
                      className={cn(
                        "mt-0.5 text-[10px] font-semibold",
                        y.dep ? "text-element-wood" : "text-destructive",
                      )}
                    >
                      {y.dep ? "Đẹp" : y.kim_lau ? "Kim Lâu" : "Tam Tai"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {result.muon_tuoi?.length ? (
            <div className="mt-4">
              <p className="text-xs font-bold text-foreground">Tuổi có thể nhờ đứng lễ</p>
              <p className="text-[11px] text-muted-foreground">
                {result.muon_tuoi_nam ? `Cho năm âm ${result.muon_tuoi_nam}: ` : ""}không phạm hạn,
                không xung/hại tuổi gia chủ — ưu tiên tuổi tam hợp, lục hợp.
              </p>
              <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                {result.muon_tuoi.map((m) => (
                  <li
                    key={m.nam_sinh}
                    className="rounded-xl border border-border/60 p-2.5 text-[12px]"
                  >
                    <p className="font-semibold text-foreground">
                      {m.can_chi} ({m.nam_sinh})
                    </p>
                    <p className="text-muted-foreground">{m.ly_do.join(" · ")}</p>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Card>
      ) : null}

      {result.hop_tuoi ? <HopTuoiCard data={result.hop_tuoi} /> : null}
      <DirectionCard result={result} />
      {result.bo_tri?.length ? <BoTriCard rows={result.bo_tri} /> : null}
      {result.nguoi_mo_hang?.length ? <MoHangCard rows={result.nguoi_mo_hang} /> : null}
      <ColorCard result={result} />

      <section>
        <div className="mb-2 flex items-end justify-between gap-2">
          <h3 className="font-display text-lg font-bold text-foreground">Ngày nên chọn</h3>
          <p className="text-[11px] text-muted-foreground">
            {result.counts.tot} ngày nên chọn · {result.counts.duoc} tạm ổn · {result.counts.tranh}{" "}
            không nên
          </p>
        </div>
        {result.best.length ? (
          <ul className="space-y-2">
            {result.best.map((d, i) => (
              <li key={d.date}>
                <button
                  type="button"
                  onClick={() => onPick(d.date)}
                  className={cn(
                    "w-full rounded-2xl border bg-card p-3 text-left transition",
                    picked === d.date
                      ? "border-primary"
                      : "border-transparent hover:border-gold-soft/60",
                  )}
                >
                  <span className="flex items-center gap-3">
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary/10 font-display text-sm font-bold text-primary">
                      {i + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-foreground">
                        {d.display}
                      </span>
                      <span className="block truncate text-[11px] text-muted-foreground">
                        {d.lunar_display} · {d.can_chi_ngay} · trực {d.truc} · {d.sao}
                      </span>
                    </span>
                    <span
                      className={cn(
                        "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                        VERDICT[d.verdict].chip,
                      )}
                    >
                      {d.score}
                    </span>
                  </span>
                  {d.huong_trung?.length ? (
                    <span className="mt-2 flex gap-1.5 pl-12 text-[11px] text-element-wood">
                      <Compass className="mt-0.5 size-3 shrink-0" />
                      Đi về {result.huong_di} gặp {d.huong_trung.join(" và ")}
                    </span>
                  ) : null}
                  {d.pham_han_nam ? (
                    <span className="mt-2 flex gap-1.5 pl-12 text-[11px] text-amber-800 dark:text-amber-300">
                      <ShieldAlert className="mt-0.5 size-3 shrink-0" />
                      {d.ly_do_xau.find((x) => x.startsWith("Năm ")) ?? "Năm này phạm hạn tuổi"}
                    </span>
                  ) : null}
                  {d.gio_tot?.length ? (
                    <span className="mt-2 flex flex-wrap gap-1.5 pl-12">
                      {d.gio_tot.map((g) => (
                        <span
                          key={g.chi}
                          className="flex items-center gap-1 rounded-full border border-gold-soft/60 px-2 py-0.5 text-[11px] text-primary"
                        >
                          <Star className="size-3 fill-amber-400 text-amber-400" />
                          Giờ {g.chi} {g.khung}
                        </span>
                      ))}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="rounded-2xl bg-card p-4 text-sm text-muted-foreground">
            Trong khoảng này chưa có ngày nào đạt. Thử nới rộng khoảng tìm.
          </p>
        )}
      </section>

      <MonthGrid days={result.days} today={result.today} picked={picked} onPick={onPick} />

      {pickedDay ? <DayDetail day={pickedDay} /> : null}

      <section className="rounded-[1.4rem] bg-primary/[0.06] p-4 text-[12px] leading-5 text-foreground/85">
        <p>
          <strong>Ghi nhớ:</strong> {result.purpose.luu_y}
        </p>
        <Link
          to="/tong-hop/$topic"
          params={{ topic: THAY_TOPIC[result.purpose.key] ?? "ngay-xuat-hanh" }}
          search={{
            ngay: askDay?.date,
            nguoi: askPeople || undefined,
            viec: result.purpose.kind,
            hoi: askQuestion,
          }}
          className="mt-3 inline-flex items-center gap-1.5 font-semibold text-primary"
        >
          {askDay
            ? `Hỏi thầy kỹ hơn về ngày ${askDay.date.slice(8)}/${askDay.date.slice(5, 7)}`
            : "Nhờ thầy chọn ngày"}{" "}
          <ArrowRight className="size-3.5" />
        </Link>
      </section>
    </div>
  );
}

function HopTuoiCard({ data }: { data: XemNgayHopTuoi }) {
  const pct = data.toi_da ? Math.round((data.diem / data.toi_da) * 100) : 0;
  return (
    <Card
      title="Hai tuổi có hợp nhau?"
      icon={<HeartHandshake className="size-4 text-primary" />}
      hint={`Chồng ${data.chong.can_chi} (${data.chong.nam_sinh_am}) · vợ ${data.vo.can_chi} (${data.vo.nam_sinh_am})`}
    >
      <div className="flex items-center gap-3">
        <span className="font-display text-3xl font-bold text-primary">
          {data.diem}
          <span className="text-base text-muted-foreground">/{data.toi_da}</span>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">{data.muc}</p>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>
      <ul className="mt-3 divide-y divide-border/40">
        {data.tieu_chi.map((t) => (
          <li key={t.key} className="flex items-start justify-between gap-3 py-2">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-foreground">
                {t.label} <span className="font-normal text-muted-foreground">· {t.chi_tiet}</span>
              </p>
              <p className="text-[12px] text-muted-foreground">{t.nhan_xet}</p>
            </div>
            <span
              className={cn(
                "shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold",
                t.diem === 2
                  ? "border-element-wood/40 text-element-wood"
                  : t.diem === 1
                    ? "border-primary/40 text-primary"
                    : "border-destructive/40 text-destructive",
              )}
            >
              {t.diem}/2
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] leading-4 text-muted-foreground">{data.ghi_chu}</p>
    </Card>
  );
}

function BoTriCard({ rows }: { rows: XemNgayBoTri[] }) {
  return (
    <Card
      title="Sắp xếp trong nhà"
      icon={<Home className="size-4 text-primary" />}
      hint="Theo cung phi người đứng tên: thứ cần đón khí thì quay về hướng cát, bếp thì ngồi chỗ hung nhìn về chỗ cát."
    >
      <ul className="space-y-2.5">
        {rows.map((row) => (
          <li key={row.key} className="rounded-xl border border-border/60 p-3">
            <p className="text-[13px] font-semibold text-foreground">{row.label}</p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {row.huong.map((h, i) => (
                <span
                  key={h.huong}
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[11px]",
                    i === 0
                      ? "border-element-wood/40 font-semibold text-element-wood"
                      : "border-border/60 text-foreground/80",
                  )}
                >
                  {h.huong} · {h.du_nien}
                </span>
              ))}
            </div>
            <p className="mt-1.5 text-[12px] leading-5 text-muted-foreground">{row.goi_y}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MoHangCard({ rows }: { rows: NonNullable<XemNgayResult["nguoi_mo_hang"]> }) {
  return (
    <Card
      title="Nhờ ai mở hàng?"
      icon={<Users className="size-4 text-primary" />}
      hint="Người bước vào hoặc mua món đầu tiên: tuổi tam hợp, lục hợp với chủ, mệnh không khắc chủ, không xung năm."
    >
      <ul className="grid gap-2 sm:grid-cols-2">
        {rows.map((m) => (
          <li key={m.nam_sinh} className="rounded-xl border border-border/60 p-2.5 text-[12px]">
            <p className="font-semibold text-foreground">
              {m.can_chi} ({m.nam_sinh})
              {m.menh ? (
                <span className="font-normal text-muted-foreground"> · {m.menh}</span>
              ) : null}
            </p>
            <p className="text-muted-foreground">{m.ly_do.join(" · ")}</p>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function DirectionCard({ result }: { result: XemNgayResult }) {
  if (!result.phuong_nam && !result.huong_goi_y && !result.huong_danh_gia) return null;
  const h = result.huong_danh_gia;
  const pn = result.phuong_nam;
  return (
    <Card
      title="Hướng theo Bát Trạch"
      icon={<Compass className="size-4 text-primary" />}
      hint="Tính từ cung phi người đứng tên, kèm các phương nên kiêng trong năm."
    >
      {h ? (
        <div
          className={cn(
            "rounded-xl border p-3",
            h.tot === undefined
              ? "border-border/60"
              : h.tot
                ? "border-element-wood/40 bg-element-wood/5"
                : "border-destructive/30 bg-destructive/5",
          )}
        >
          <p className="text-sm font-semibold text-foreground">
            Bạn đang ở hướng {h.huong} · sơn {h.son} ({h.range})
          </p>
          {h.du_nien ? (
            <p
              className={cn("mt-0.5 text-[13px]", h.tot ? "text-element-wood" : "text-destructive")}
            >
              {h.du_nien} ({h.muc}) — {h.tom_tat}
            </p>
          ) : (
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              Cần giới tính người đứng tên mới tính được cung phi.
            </p>
          )}
          {h.canh_bao.map((c) => (
            <p key={c} className="mt-1 flex gap-1.5 text-[12px] text-amber-800 dark:text-amber-300">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {c}
            </p>
          ))}
        </div>
      ) : null}
      {result.huong_goi_y?.length ? (
        <div className="mt-3">
          <p className="text-xs font-bold text-foreground">Bốn hướng cát theo cung phi</p>
          <ul className="mt-1.5 divide-y divide-border/40">
            {result.huong_goi_y.map((g) => (
              <li key={g.huong} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">
                    {g.huong}{" "}
                    <span className="text-[11px] font-normal text-element-wood">· {g.du_nien}</span>
                    {g.pham_tam_sat ? (
                      <span className="ml-1 text-[10px] font-semibold text-destructive">
                        (phạm Tam Sát năm nay)
                      </span>
                    ) : null}
                  </p>
                  <p className="text-[11px] text-muted-foreground">{g.tom_tat}</p>
                </div>
                <span className="shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[11px] text-muted-foreground">
                  {g.range}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {pn ? (
        <p className="mt-3 rounded-xl bg-muted/40 px-3 py-2 text-[12px] leading-5 text-foreground/80">
          Năm {pn.chi_nam}: <strong>Tam Sát</strong> ở phương {pn.tam_sat.phuong} (
          {pn.tam_sat.son.join(", ")}) · <strong>Thái Tuế</strong> sơn {pn.thai_tue} ·{" "}
          <strong>Tuế Phá</strong> sơn {pn.tue_pha} — năm nay tránh đào móng, đập phá về các phía
          đó.
        </p>
      ) : null}
    </Card>
  );
}

function ColorCard({ result }: { result: XemNgayResult }) {
  if (!result.mau_goi_y) return null;
  const m = result.mau_danh_gia;
  const Row = ({ label, items, tone }: { label: string; items: string[]; tone: string }) =>
    items.length ? (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className={cn("w-20 text-[11px] font-semibold", tone)}>{label}</span>
        {items.map((c) => (
          <span
            key={c}
            className="flex items-center gap-1 rounded-full border border-border/60 py-0.5 pl-1 pr-2 text-[11px]"
          >
            <span
              className="size-3 rounded-full border border-black/10"
              style={{ backgroundColor: COLOR_HEX[c] }}
            />
            {c}
          </span>
        ))}
      </div>
    ) : null;
  return (
    <Card title="Màu xe với mệnh chủ xe" icon={<Palette className="size-4 text-primary" />}>
      {m ? (
        <p
          className={cn(
            "mb-3 rounded-xl p-3 text-[13px]",
            m.muc === "rat_hop" || m.muc === "hop"
              ? "bg-element-wood/10 text-element-wood"
              : m.muc === "tam_duoc"
                ? "bg-primary/10 text-foreground"
                : "bg-destructive/10 text-destructive",
          )}
        >
          {m.nhan_xet}
        </p>
      ) : null}
      <div className="space-y-2">
        <Row label="Tốt nhất" items={result.mau_goi_y.tot_nhat} tone="text-element-wood" />
        <Row label="Hợp" items={result.mau_goi_y.hop} tone="text-primary" />
        <Row label="Né" items={result.mau_goi_y.tranh} tone="text-destructive" />
      </div>
    </Card>
  );
}

/** Lưới ngày tô màu theo mức — bấm một ô để xem chi tiết ngày đó. */
function MonthGrid({
  days,
  today,
  picked,
  onPick,
}: {
  days: XemNgayDay[];
  today: string;
  picked: string | null;
  onPick: (d: string) => void;
}) {
  const months = useMemo(() => {
    const out: { key: string; label: string; days: XemNgayDay[] }[] = [];
    for (const d of days) {
      const key = d.date.slice(0, 7);
      let m = out.find((x) => x.key === key);
      if (!m) {
        m = { key, label: `Tháng ${Number(key.slice(5))}/${key.slice(0, 4)}`, days: [] };
        out.push(m);
      }
      m.days.push(d);
    }
    return out;
  }, [days]);
  // Khoảng dài (cả năm) thì mặc định chỉ mở 2 tháng đầu cho đỡ dài trang.
  const [showAll, setShowAll] = useState(false);
  useEffect(() => setShowAll(false), [days]);
  const visible = showAll ? months : months.slice(0, 2);

  return (
    <section className="almanac-panel p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display text-base font-bold text-foreground">Cả khoảng ngày</h3>
        <div className="flex gap-3 text-[10px] text-muted-foreground">
          {(Object.keys(VERDICT) as XemNgayVerdict[]).map((v) => (
            <span key={v} className="flex items-center gap-1">
              <span className={cn("size-2.5 rounded-sm", VERDICT[v].cell)} />
              {VERDICT[v].label}
            </span>
          ))}
        </div>
      </div>
      {visible.map((m) => {
        const first = new Date(`${m.days[0].date}T00:00:00Z`).getUTCDay();
        const offset = (first + 6) % 7; // Thứ Hai = 0
        return (
          <div key={m.key} className="mt-4">
            <p className="mb-1.5 text-xs font-semibold text-foreground/80">{m.label}</p>
            <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-muted-foreground">
              {["T2", "T3", "T4", "T5", "T6", "T7", "CN"].map((w) => (
                <span key={w}>{w}</span>
              ))}
              {Array.from({ length: offset }, (_, i) => (
                <span key={`pad-${i}`} />
              ))}
              {m.days.map((d) => (
                <button
                  key={d.date}
                  type="button"
                  onClick={() => onPick(d.date)}
                  aria-label={`${d.display}: ${VERDICT[d.verdict].label}`}
                  className={cn(
                    "flex h-11 flex-col items-center justify-center rounded-lg text-[12px] font-semibold transition sm:h-12",
                    VERDICT[d.verdict].cell,
                    picked === d.date && "ring-2 ring-primary ring-offset-1 ring-offset-card",
                    d.date < today && "opacity-40",
                  )}
                >
                  {Number(d.date.slice(8))}
                  <span className="text-[8px] font-normal opacity-80">
                    {d.lunar_display.slice(0, 5).replace(/^0/, "")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {months.length > 2 ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-3 w-full rounded-xl border border-border/60 py-2 text-xs font-semibold text-primary"
        >
          {showAll ? "Thu gọn" : `Mở thêm ${months.length - 2} tháng`}
        </button>
      ) : null}
    </section>
  );
}

function DayDetail({ day }: { day: XemNgayDay }) {
  return (
    <section className="rounded-[1.4rem] border border-primary/30 bg-card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-display text-base font-bold text-foreground">{day.display}</h3>
          <p className="text-[11px] text-muted-foreground">
            {day.lunar_display} · ngày {day.can_chi_ngay} · trực {day.truc} · sao {day.sao} (
            {day.is_hoang_dao ? "hoàng đạo" : "hắc đạo"})
          </p>
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
            VERDICT[day.verdict].chip,
          )}
        >
          {VERDICT[day.verdict].label} · {day.score}
        </span>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {day.ly_do_tot.length ? (
          <ul className="space-y-1">
            {day.ly_do_tot.map((r) => (
              <li key={r} className="flex gap-1.5 text-[12px] text-element-wood">
                <ShieldCheck className="mt-0.5 size-3.5 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        ) : null}
        {day.ly_do_xau.length ? (
          <ul className="space-y-1">
            {day.ly_do_xau.map((r) => (
              <li key={r} className="flex gap-1.5 text-[12px] text-destructive">
                <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> {r}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="mt-3 space-y-1.5 border-t border-border/60 pt-3 text-[12px] text-foreground/85">
        <p className="flex flex-wrap items-center gap-1.5">
          <Clock className="size-3.5 text-primary" />
          <span className="font-semibold">Giờ hoàng đạo:</span>
          {day.gio_hoang_dao.map((g) => `${g.gio_chi} (${g.khung_gio})`).join(", ")}
        </p>
        {day.huong ? (
          <p className="flex flex-wrap items-center gap-1.5">
            <Compass className="size-3.5 text-primary" />
            <span className="font-semibold">Ra cửa đi về:</span> Hỷ Thần {day.huong.hy_than} · Tài
            Thần {day.huong.tai_than}
          </p>
        ) : null}
      </div>
    </section>
  );
}
